import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, CreditCard, Building2 } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

function humanize(key) {
  return String(key || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * TicketIntakeDetails (SOUND-510) — the "Request details" card on the ticket
 * workspace. Renders the parent ticket's collected template intake values
 * (`ticket.fields`) as label → value rows. `mid-ref` / `company-ref` values
 * render as LINKS to the entity. Field labels + types come from the ticket's
 * template (fetched by `templateId` — internal-only endpoint; non-internal
 * viewers get humanized keys + plain values). This COMPLEMENTS the Linked panel
 * (SOUND-509 turns refs into ticketLinks there); it doesn't duplicate it — this
 * is the structured intake the requester submitted.
 */
export default function TicketIntakeDetails({ ticket }) {
  const { user } = useAuth();
  const internal = isInternal(user);
  const companyId = useEffectiveCompanyId();
  const [fieldDefs, setFieldDefs] = useState(null);

  const fields = ticket?.fields || null;
  const templateId = ticket?.templateId || null;

  useEffect(() => {
    if (!fields || !templateId || !internal || !user) return;
    let cancelled = false;
    makeApiRequest(
      "get",
      apiRoutes.getTicketTemplate(templateId),
      { id: user.id, accessToken: user.accessToken },
      companyId
    )
      .then((res) => {
        if (!cancelled) setFieldDefs(res.data?.data?.intakeFields || []);
      })
      .catch(() => {
        if (!cancelled) setFieldDefs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [templateId, fields, internal, companyId, user]);

  if (!fields || Object.keys(fields).length === 0) return null;

  const defs = fieldDefs || [];
  const defByKey = {};
  defs.forEach((d) => (defByKey[d.key] = d));
  const otherKeys = new Set(
    defs.filter((d) => d.type === "select-other" && d.otherKey).map((d) => d.otherKey)
  );

  // Ordered rows: template field order first (skipping select-other companions,
  // which render inline with their parent), then any drift keys not in the
  // template. Companion `otherKey` values are folded into the parent row.
  const rows = [];
  const used = new Set();
  defs.forEach((d) => {
    if (otherKeys.has(d.key)) return;
    if (Object.prototype.hasOwnProperty.call(fields, d.key)) {
      rows.push({ key: d.key, def: d });
      used.add(d.key);
      if (d.otherKey) used.add(d.otherKey);
    }
  });
  Object.keys(fields).forEach((k) => {
    if (!used.has(k) && !otherKeys.has(k)) rows.push({ key: k, def: defByKey[k] || null });
  });

  if (rows.length === 0) return null;

  return (
    <section className="surface p-5">
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <ClipboardList className="h-4 w-4" />
        Request details
      </div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {rows.map(({ key, def }) => (
          <div key={key} className={def?.gridCols === 2 ? "" : "sm:col-span-2"}>
            <dt className="text-xs text-muted-foreground">{def?.label || humanize(key)}</dt>
            <dd className="mt-0.5 text-sm text-foreground">
              <IntakeValue value={fields[key]} def={def} fields={fields} />
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function IntakeValue({ value, def, fields }) {
  const type = def?.type;

  if (type === "mid-ref" && value) {
    return (
      <Link to={`/mids/${value}`} className="inline-flex items-center gap-1 font-medium text-brand hover:underline">
        <CreditCard className="h-3.5 w-3.5" />
        View MID
      </Link>
    );
  }
  if (type === "company-ref" && value) {
    return (
      <Link to={`/companies/${value}`} className="inline-flex items-center gap-1 font-medium text-brand hover:underline">
        <Building2 className="h-3.5 w-3.5" />
        View company
      </Link>
    );
  }
  if (type === "boolean") {
    // SOUND-573: unanswered must NOT read as "No" — that asserts an answer the
    // requester never gave, the same conflation the input control had. Legacy
    // string forms stay readable: tickets created while this field was a `select`
    // stored "Yes"/"No" and survive until normalization runs.
    if (value === undefined || value === null || value === "") {
      return <span className="text-muted-foreground">—</span>;
    }
    const yes = value === true || value === "true" || value === "Yes" || value === "yes";
    return <span>{yes ? "Yes" : "No"}</span>;
  }
  if (type === "date" && value) {
    const d = new Date(value);
    return <span>{isNaN(d.getTime()) ? String(value) : d.toLocaleDateString()}</span>;
  }
  if (type === "select-other") {
    const other = def?.otherKey ? fields[def.otherKey] : null;
    return (
      <span className="whitespace-pre-wrap">
        {String(value ?? "—")}
        {other ? ` — ${other}` : ""}
      </span>
    );
  }
  if (value === "" || value === null || value === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  return <span className="whitespace-pre-wrap">{String(value)}</span>;
}
